import { marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import { extensionMentions, replaceMentions } from '@/common/marked/extension-mentions/extension-mentions';
import { extensionClickableOption } from '@/common/marked/extension-clickable-option/extension-clickable-option';

// Define a custom renderer to control spacing
const renderer = {
    paragraph (text) {
        return `<p>${text}</p>`;
    },
    list (body, ordered) {
        const tag = ordered ? 'ol' : 'ul';

        return `<${tag}>${body}</${tag}>`;
    },
    listitem (text) {
        // Check if the list item contains a clickable option (which may contain mentions)
        const hasClickableOption = /<span class="clickable-option"/.test(text);

        if (hasClickableOption) {
            return `<li data-clickable-list-item="true">${text}</li>`;
        }

        return `<li>${text}</li>`;
    },
    blockquote (quote) {
        return `<blockquote>${quote}</blockquote>`;
    },
    heading (text, level) {
        return `<h${level}>${text}</h${level}>`;
    },
};

// Configure marked with custom renderer
marked.use({
    gfm: true,
    breaks: true,
    renderer,
    extensions: [extensionMentions, extensionClickableOption],
});

// Sanitization configuration
const sanitizeOptions = {
    allowedTags: [
        'b',
        'strong',
        'i',
        'em',
        'p',
        'ul',
        'ol',
        'li',
        'a',
        'code',
        'circle',
        'pre',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'u',
        'del',
        's',
        'blockquote',
        'table',
        'th',
        'tr',
        'td',
        'input',
        'line',
        'rect',
        'span',
        'hr',
        'svg',
        'path',
        'br',
    ],
    allowedAttributes: {
        'a': ['href', 'target', 'rel'],
        'input': ['type', 'checked', 'disabled'],
        'circle': ['cx', 'cy', 'r'],
        'line': ['x1', 'y1', 'x2', 'y2'],
        'rect': ['x', 'y', 'width', 'height', 'rx', 'ry'],
        'span': ['data-type', 'data-id', 'data-text'],
        'li': ['data-clickable-list-item'],
        'svg': ['width', 'height', 'viewBox'],
        'path': ['d'],
    },
    allowedClasses: {
        span: ['mention', 'clickable-option'],
    },
    parser: {
        lowerCaseAttributeNames: false, // allows `viewBox`
    },
    transformTags: {
        a: (tagName, attribs) => ({
            tagName,
            attribs: {
                ...attribs,
                target: '_blank',
                rel: 'noopener noreferrer',
            },
        }),
        input: (tagName, attribs) => {
            // Only allow checkbox inputs
            if (attribs.type === 'checkbox') {
                return {
                    tagName,
                    attribs,
                };
            }

            return false;
        },
    },
};

/**
 * Render markdown text to sanitized HTML or plaintext
 *
 * @param {string} markdownText - Raw markdown text to parse
 * @param {string} as - Rendering format, can be 'html' or 'plaintext', defaulting to 'html'
 * @returns {string} Sanitized HTML or plaintext
 */
export function renderMarkdown (markdownText, as = 'html') {
    if (!markdownText) return '';

    return as === 'plaintext' ? renderMarkdownToPlaintext(markdownText) : renderMarkdownToHtml(markdownText);
}

function renderMarkdownToHtml (markdownText) {
    const html = marked.parse(markdownText);

    return sanitizeHtml(html, sanitizeOptions);
}

function renderMarkdownToPlaintext (markdownText) {
    return replaceMentions(markdownText);
}
